import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    type BankAccountDisplayDetail,
    type IncomeSplitConfig,
    IncomeSplitDetails,
    type IncomeSplitRule,
    type IncomeSplitRuleSet,
    type PaycheckRoutingRuleAccountMetadata,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import IncomeSplitEndOfRules from '@fruitful/income-split/src/components/IncomeSplitEndOfRules';
import { useAtomValue } from 'jotai';
import React, { useMemo, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronRightIcon,
    H5Emphasized,
    H6Emphasized,
    Icon,
    Input,
    LabelRegular,
    MonoLabelRegular,
    ScrollView,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { FullPageContainer } from '../../common/components/GroveContainers';
import { MutationErrorToastOptions } from '../../common/components/Toasts';
import { adminUserAtom } from '../../entities/entityAtoms';
import type { IncomeSplitExecutionTransaction } from '../../entities/entityTypes';
import {
    LineageAuditBadge,
    LineageAuditPanel,
} from '../../entities/membership/components/paycheckSplit/LineageAudit';
import {
    type BulkSimulationResponse,
    type BulkSimulationResultItem,
    type IncomeSplitPlanResponse,
    type LineageAuditSweepMember,
    type LineageAuditSweepResponse,
    lineageVerdict,
    type RulePlan,
    type RuleSetPlan,
    type RuleValidationDetail,
    type SimulatorLineageFinding,
    useIncomeSplitLineageAuditSweep,
    useSimulateBulkIncomeSplit,
} from '../../entities/membership/hooks/useSimulateIncomeSplit';

const parseMembershipIds = (value: string): string[] | undefined => {
    const ids = value
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '');

    return ids.length > 0 ? ids : undefined;
};

const parseLimit = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (trimmed === '') {
        return undefined;
    }

    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const getDatadogIncomeSplitExecutionUrl = (incomeSplitExecutionId: string) =>
    `https://app.datadoghq.com/logs?query=${encodeURIComponent(
        `@incomeSplitExecutionId:${incomeSplitExecutionId}`,
    )}`;

type StatusFilter =
    | 'all'
    | 'simulated'
    | 'succeeded'
    | 'failed'
    | 'validation_errors'
    | 'lineage_issues';

const STATUS_FILTER_PREDICATES: Record<
    StatusFilter,
    (row: BulkSimulationResultItem) => boolean
> = {
    all: () => true,
    simulated: (row) => row.simulationResult != null,
    succeeded: (row) => row.status === 'success',
    failed: (row) => row.status === 'error' || row.incomeSplitConfig == null,
    validation_errors: (row) => row.validationErrors.length > 0,
    lineage_issues: (row) => lineageVerdict(row.lineageAudit) !== 'pass',
};

const SummaryTile = ({
    label,
    value,
    tone = 'neutral',
    isActive = false,
    onPress,
}: {
    label: string;
    value: number;
    tone?: 'neutral' | 'positive' | 'danger';
    isActive?: boolean;
    onPress?: () => void;
}) => {
    const color =
        tone === 'positive'
            ? '$green900'
            : tone === 'danger'
              ? '$red1000'
              : '$gray1200';

    return (
        <YStack
            backgroundColor={isActive ? '$green300' : '$gray100'}
            borderRadius={16}
            padding={16}
            minWidth={150}
            gap={4}
            cursor={onPress != null ? 'pointer' : undefined}
            onPress={onPress}
            borderWidth={isActive ? 2 : 0}
            borderColor={isActive ? '$green900' : 'transparent'}
            hoverStyle={
                onPress != null ? { backgroundColor: '$gray200' } : undefined
            }
        >
            <LabelRegular color="$gray1000">{label}</LabelRegular>
            <H6Emphasized color={color}>{value}</H6Emphasized>
        </YStack>
    );
};

const StatusPill = ({
    status,
}: {
    status: BulkSimulationResultItem['status'];
}) => (
    <View
        backgroundColor={status === 'success' ? '$green300' : '$red200'}
        borderRadius={16}
        paddingHorizontal={8}
        paddingVertical={4}
        alignSelf="flex-start"
    >
        <MonoLabelRegular color="$gray1200">{status}</MonoLabelRegular>
    </View>
);

const ErrorBody = (row: BulkSimulationResultItem) => {
    if (row.error != null && row.error !== '') {
        return (
            <BodySmallRegular color="$red1000">{row.error}</BodySmallRegular>
        );
    }

    if (row.validationErrors.length > 0) {
        return (
            <YStack gap={4}>
                {row.validationErrors.map((error) => (
                    <BodySmallRegular key={error} color="$orange1100">
                        {error}
                    </BodySmallRegular>
                ))}
            </YStack>
        );
    }

    return <BodySmallRegular color="$gray1000">None</BodySmallRegular>;
};

const SimulationSummaryBody = (row: BulkSimulationResultItem) => {
    const result = row.simulationResult;
    if (result == null) {
        return (
            <BodySmallRegular color="$gray1000">No simulation</BodySmallRegular>
        );
    }

    return (
        <YStack gap={4}>
            <BodySmallRegular>
                Allocated:{' '}
                {centsToFormattedDollars(result.totalAllocatedAmount, false)}
            </BodySmallRegular>
            <BodySmallRegular>
                Remaining:{' '}
                {centsToFormattedDollars(result.finalRemainingFunds, false)}
            </BodySmallRegular>
            <BodySmallRegular color="$gray1000">
                Rule sets: {result.ruleSetPlans.length}
            </BodySmallRegular>
        </YStack>
    );
};

const SourceBody = (row: BulkSimulationResultItem) => (
    <YStack gap={4}>
        <MonoLabelRegular>{row.sourceSyncteraAccountId}</MonoLabelRegular>
        <BodySmallRegular color="$gray1000">
            {centsToFormattedDollars(row.amountCents, false)}
        </BodySmallRegular>
    </YStack>
);

const getRulePlanAccountMetadata = (
    rule: RulePlan,
): PaycheckRoutingRuleAccountMetadata | undefined => {
    const destinationAccount =
        rule.accountType === 'INTERNAL_INVESTMENT'
            ? (rule.destinationInternalInvestmentAccountDetails ??
              rule.destinationAccountDetails)
            : (rule.destinationDepositoryAccountDetails ??
              rule.destinationAccountDetails);

    if (destinationAccount == null) {
        return undefined;
    }

    return {
        nickname: destinationAccount.nickname,
        accountNumber: destinationAccount.accountNumber,
        accountNumberMasked: destinationAccount.accountNumberMasked,
    };
};

const getCurrentRuleMetadataById = (
    row: BulkSimulationResultItem,
): Map<string, PaycheckRoutingRuleAccountMetadata> => {
    const metadataById = new Map<string, PaycheckRoutingRuleAccountMetadata>();

    row.simulationResult?.ruleSetPlans.forEach((ruleSet) => {
        ruleSet.rules.forEach((rule) => {
            const metadata = getRulePlanAccountMetadata(rule);
            if (metadata == null) {
                return;
            }

            [
                rule.id,
                rule.originalId,
                rule.originalRule?.id,
                rule.originalRule?.originalId,
            ]
                .filter((id): id is string => id != null && id !== '')
                .forEach((id) => {
                    metadataById.set(id, metadata);
                });
        });
    });

    return metadataById;
};

const withCurrentRuleAccountMetadata = (
    incomeSplitConfig: IncomeSplitConfig,
    metadataById: Map<string, PaycheckRoutingRuleAccountMetadata>,
): IncomeSplitConfig => ({
    ...incomeSplitConfig,
    ruleSets: incomeSplitConfig.ruleSets.map((ruleSet) => ({
        ...ruleSet,
        rules: ruleSet.rules.map((rule): IncomeSplitRule => {
            const metadata =
                metadataById.get(rule.id ?? '') ??
                metadataById.get(rule.originalId ?? '');

            return metadata != null
                ? {
                      ...rule,
                      accountMetadata: metadata,
                  }
                : rule;
        }),
    })),
});

const getTotalEligibleAccounts = (
    incomeSplitConfig: IncomeSplitConfig,
): BankAccountDisplayDetail[] =>
    incomeSplitConfig.accounts.map((account) => ({
        bankAccountId: account.accountId,
    })) as BankAccountDisplayDetail[];

const CurrentIncomeSplitDetails = ({
    incomeSplitConfig,
}: {
    incomeSplitConfig: IncomeSplitConfig;
}) => {
    return (
        <IncomeSplitDetails
            incomeSplit={incomeSplitConfig}
            totalEligibleAccounts={getTotalEligibleAccounts(incomeSplitConfig)}
            statusBadge={
                <View
                    backgroundColor={
                        incomeSplitConfig.status ===
                        PaycheckRoutingStatus.ACTIVE
                            ? '$green300'
                            : '$red200'
                    }
                    borderRadius={16}
                    paddingHorizontal={8}
                    paddingVertical={4}
                >
                    <MonoLabelRegular color="$gray1200">
                        {incomeSplitConfig.status ?? 'UNKNOWN'}
                    </MonoLabelRegular>
                </View>
            }
            verifyNumberOfAccountsWatched={false}
        />
    );
};

const getSortedCurrentRuleSets = (
    incomeSplitConfig: IncomeSplitConfig,
): IncomeSplitRuleSet[] =>
    [...incomeSplitConfig.ruleSets].sort((a, b) => a.priority - b.priority);

const getMatchingSimulationRuleSet = (
    ruleSet: IncomeSplitRuleSet,
    simulationRuleSets: RuleSetPlan[],
): RuleSetPlan | undefined =>
    simulationRuleSets.find((simulationRuleSet) => {
        return simulationRuleSet.ruleSetId === ruleSet.id;
    });

const formatRulePercentage = (percentage: string | number | undefined) => {
    if (percentage == null) {
        return 'N/A';
    }

    const numericPercentage = Number(percentage);
    if (!Number.isFinite(numericPercentage)) {
        return 'N/A';
    }

    return `${Math.floor(numericPercentage * 100)}%`;
};

const RuleDataTag = ({ text, color }: { text: string; color: string }) => (
    <View
        alignSelf="flex-start"
        backgroundColor="$gray100"
        borderRadius={16}
        paddingVertical={4}
        paddingHorizontal={6}
    >
        <MonoLabelRegular color={color}>{text}</MonoLabelRegular>
    </View>
);

const RuleDataRow = ({ children }: { children: React.ReactNode }) => (
    <XStack gap={8} alignItems="center" flexWrap="wrap">
        <>{children}</>
    </XStack>
);

const RuleConfigRows = ({
    percentage,
    targetBalanceCents,
    monthlyContributionMaxCents,
    yearlyContributionMaxCents,
}: {
    percentage: string | number | undefined;
    targetBalanceCents?: number | null;
    monthlyContributionMaxCents?: number | null;
    yearlyContributionMaxCents?: number | null;
}) => {
    const hasTargetBalance = (targetBalanceCents ?? 0) > 0;
    const hasMonthlyLimit = (monthlyContributionMaxCents ?? 0) > 0;
    const hasYearlyLimit = (yearlyContributionMaxCents ?? 0) > 0;

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                Rule Configuration
            </BodySmallRegular>
            <RuleDataRow>
                <RuleDataTag text="CONTRIBUTE" color="$gray1200" />
                <RuleDataTag
                    text={formatRulePercentage(percentage)}
                    color="$green900"
                />
            </RuleDataRow>
            {hasTargetBalance && (
                <RuleDataRow>
                    <RuleDataTag text="UNTIL" color="$gray1200" />
                    <MonoLabelRegular color="$gray1200">
                        available balance =
                    </MonoLabelRegular>
                    <RuleDataTag
                        text={centsToFormattedDollars(
                            targetBalanceCents ?? 0,
                            false,
                        )}
                        color="$green900"
                    />
                </RuleDataRow>
            )}
            {hasMonthlyLimit && (
                <RuleDataRow>
                    <RuleDataTag text="UNTIL" color="$gray1200" />
                    <MonoLabelRegular color="$gray1200">
                        monthly contribution =
                    </MonoLabelRegular>
                    <RuleDataTag
                        text={centsToFormattedDollars(
                            monthlyContributionMaxCents ?? 0,
                            false,
                        )}
                        color="$green900"
                    />
                </RuleDataRow>
            )}
            {hasYearlyLimit && (
                <RuleDataRow>
                    <RuleDataTag text="UNTIL" color="$gray1200" />
                    <MonoLabelRegular color="$gray1200">
                        annual contribution =
                    </MonoLabelRegular>
                    <RuleDataTag
                        text={centsToFormattedDollars(
                            yearlyContributionMaxCents ?? 0,
                            false,
                        )}
                        color="$green900"
                    />
                </RuleDataRow>
            )}
        </YStack>
    );
};

const getNoTransferReason = (rule: RulePlan) => {
    const { limitDetails } = rule;
    if (limitDetails == null) {
        return null;
    }

    const reasons: string[] = [];
    if (limitDetails.monthlyMet) {
        reasons.push('Monthly limit reached');
    }
    if (limitDetails.yearlyMet) {
        reasons.push('Yearly limit reached');
    }
    if (limitDetails.hasReachedCapacity) {
        reasons.push('Capacity reached');
    }

    return reasons.length > 0 ? reasons.join(', ') : null;
};

const ActualTransferAmountTag = ({
    transferType,
    transferAmount,
}: {
    transferType: string;
    transferAmount: number;
}) => (
    <RuleDataTag
        text={
            transferType === 'NO_TRANSFER'
                ? 'NONE'
                : centsToFormattedDollars(transferAmount, false)
        }
        color={transferType === 'NO_TRANSFER' ? '$gray1000' : '$green900'}
    />
);

const RemainingMonthlyCapacity = ({ rule }: { rule: RulePlan }) => {
    const remainingMonthlyCapacity =
        rule.limitDetails?.remainingMonthlyCapacity;
    if (
        rule.transferInfo.type !== 'NO_TRANSFER' ||
        remainingMonthlyCapacity == null
    ) {
        return null;
    }

    return (
        <MonoLabelRegular color="$gray1200" fontSize={12}>
            Remaining monthly capacity:{' '}
            {centsToFormattedDollars(remainingMonthlyCapacity, false)}
        </MonoLabelRegular>
    );
};

const AccountTransferRoute = ({
    rule,
    sourceAccount,
}: {
    rule: RulePlan;
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
}) => {
    const destinationAccount = getRulePlanAccountMetadata(rule);

    return (
        <YStack padding={8} borderRadius={8} backgroundColor="$gray100" gap={4}>
            {sourceAccount != null && (
                <XStack justifyContent="space-between" gap={8}>
                    <BodySmallRegular color="$gray1000">From</BodySmallRegular>
                    <YStack alignItems="flex-end" flex={1} minWidth={0}>
                        <BodySmallRegular color="$gray1200">
                            {sourceAccount.nickname}
                        </BodySmallRegular>
                        <MonoLabelRegular color="$gray1000">
                            {sourceAccount.accountNumberMasked}
                        </MonoLabelRegular>
                    </YStack>
                </XStack>
            )}
            {destinationAccount != null && (
                <XStack justifyContent="space-between" gap={8}>
                    <BodySmallRegular color="$gray1000">
                        Destination
                    </BodySmallRegular>
                    <YStack alignItems="flex-end" flex={1} minWidth={0}>
                        <BodySmallRegular color="$gray1200">
                            {destinationAccount.nickname}
                        </BodySmallRegular>
                        {(destinationAccount.accountNumberMasked ??
                            destinationAccount.accountNumber) != null && (
                            <MonoLabelRegular color="$gray1000">
                                {destinationAccount.accountNumberMasked ??
                                    destinationAccount.accountNumber}
                            </MonoLabelRegular>
                        )}
                    </YStack>
                </XStack>
            )}
        </YStack>
    );
};

const getRuleLookupIds = ({
    expectedRule,
    actualRule,
}: {
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
}) =>
    [
        expectedRule?.id,
        expectedRule?.originalId,
        actualRule?.id,
        actualRule?.originalId,
        actualRule?.originalRule?.id,
        actualRule?.originalRule?.originalId,
    ].filter((id): id is string => id != null && id !== '');

const getYtdTransactionsForRule = ({
    row,
    expectedRule,
    actualRule,
}: {
    row: BulkSimulationResultItem;
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
}): IncomeSplitExecutionTransaction[] => {
    const transactionsById = new Map<string, IncomeSplitExecutionTransaction>();

    getRuleLookupIds({ expectedRule, actualRule }).forEach((ruleId) => {
        row.ytdTransactionsByRuleId[ruleId]?.forEach((transaction) => {
            transactionsById.set(transaction.id, transaction);
        });
    });

    return [...transactionsById.values()].sort(
        (a, b) =>
            new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime(),
    );
};

const YtdTransactionRows = ({
    transactions,
}: {
    transactions: IncomeSplitExecutionTransaction[];
}) => {
    const [isOpen, setIsOpen] = useState(false);

    if (transactions.length === 0) {
        return null;
    }

    const nonZeroTransactions = transactions.filter(
        (transaction) => transaction.amount !== 0,
    );
    if (nonZeroTransactions.length === 0) {
        return null;
    }

    return (
        <YStack gap={6}>
            <XStack
                onPress={() => {
                    setIsOpen((current) => !current);
                }}
                cursor="pointer"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
            >
                <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                    YTD transfers used in this calculation (
                    {nonZeroTransactions.length})
                </BodySmallRegular>
                <Icon
                    icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
                    size={16}
                    color="$gray1000"
                />
            </XStack>
            {isOpen &&
                nonZeroTransactions.map((transaction) => (
                    <XStack
                        key={transaction.id}
                        padding={8}
                        borderRadius={8}
                        backgroundColor="$gray100"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={8}
                    >
                        <YStack gap={2} flex={1} minWidth={0}>
                            <MonoLabelRegular color="$gray1000">
                                {new Date(
                                    transaction.dateAdded,
                                ).toLocaleDateString()}
                            </MonoLabelRegular>
                            <BodySmallRegular color="$gray1000">
                                {transaction.destinationAccountDisplayName}
                            </BodySmallRegular>
                        </YStack>
                        <YStack gap={2} alignItems="flex-end">
                            <MonoLabelRegular color="$gray1200">
                                {centsToFormattedDollars(
                                    transaction.amount,
                                    false,
                                )}
                            </MonoLabelRegular>
                            <MonoLabelRegular
                                color={
                                    transaction.status === 'SUCCESS'
                                        ? '$green900'
                                        : '$gray1000'
                                }
                            >
                                {transaction.status}
                            </MonoLabelRegular>
                        </YStack>
                    </XStack>
                ))}
        </YStack>
    );
};

const ActualResultDetails = ({
    rule,
    ytdTransactions,
    sourceAccount,
}: {
    rule: RulePlan;
    ytdTransactions: IncomeSplitExecutionTransaction[];
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
}) => {
    const transferType = rule.transferInfo.type;
    const transferAmount = rule.transferInfo.amountToTransfer;
    const noTransferReason = getNoTransferReason(rule);

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                Actual Result
            </BodySmallRegular>
            <AccountTransferRoute rule={rule} sourceAccount={sourceAccount} />
            <RuleDataRow>
                <RuleDataTag text="TRANSFER" color="$gray1200" />
                <ActualTransferAmountTag
                    transferType={transferType}
                    transferAmount={transferAmount}
                />
            </RuleDataRow>
            {noTransferReason != null && noTransferReason !== '' && (
                <MonoLabelRegular color="$red800" fontSize={12}>
                    {noTransferReason}
                </MonoLabelRegular>
            )}
            <RemainingMonthlyCapacity rule={rule} />
            <YtdTransactionRows transactions={ytdTransactions} />
        </YStack>
    );
};

const getRuleAccountMetadata = ({
    expectedRule,
    actualRule,
}: {
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
}): PaycheckRoutingRuleAccountMetadata | undefined =>
    expectedRule?.accountMetadata ??
    (actualRule != null ? getRulePlanAccountMetadata(actualRule) : undefined);

const RuleAccountHeader = ({
    accountName,
    accountNumber,
}: {
    accountName: string;
    accountNumber?: string;
}) => (
    <YStack gap={4}>
        <BodyMediumRegular>{accountName}</BodyMediumRegular>
        {accountNumber != null && (
            <LabelRegular color="$gray1000">{accountNumber}</LabelRegular>
        )}
    </YStack>
);

const getRuleAccountName = ({
    accountMetadata,
    expectedRule,
    actualRule,
}: {
    accountMetadata?: PaycheckRoutingRuleAccountMetadata;
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
}) =>
    accountMetadata?.nickname ??
    expectedRule?.description ??
    expectedRule?.accountId ??
    actualRule?.accountId ??
    'Unknown rule';

const getRuleAccountNumber = (
    accountMetadata?: PaycheckRoutingRuleAccountMetadata,
) =>
    accountMetadata?.accountNumberMasked ??
    accountMetadata?.accountNumber ??
    undefined;

const ExpectedRulePanel = ({ rule }: { rule?: IncomeSplitRule }) => {
    if (rule == null) {
        return (
            <YStack padding={12} borderRadius={8} backgroundColor="$white">
                <BodySmallRegular color="$gray1000">
                    No matching expected rule.
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <RuleConfigRows
            percentage={rule.percentage}
            targetBalanceCents={rule.targetBalanceCents}
            monthlyContributionMaxCents={rule.monthlyContributionMaxCents}
            yearlyContributionMaxCents={rule.yearlyContributionMaxCents}
        />
    );
};

const ActualRulePanel = ({
    rule,
    ytdTransactions,
    sourceAccount,
}: {
    rule?: RulePlan;
    ytdTransactions: IncomeSplitExecutionTransaction[];
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
}) => {
    if (rule == null) {
        return (
            <YStack padding={12} borderRadius={8} backgroundColor="$white">
                <BodySmallRegular color="$gray1000">
                    This rule did not run in the simulation.
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <ActualResultDetails
            rule={rule}
            ytdTransactions={ytdTransactions}
            sourceAccount={sourceAccount}
        />
    );
};

const ExperimentalValidationDetail = ({
    validationDetail,
}: {
    validationDetail?: RuleValidationDetail;
}) => {
    if (validationDetail == null) {
        return null;
    }

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={4}>
            <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                EXPERIMENTAL: Validation:{' '}
                {validationDetail.isValid ? 'valid' : 'invalid'}
            </BodySmallRegular>
            {validationDetail.reason != null &&
                validationDetail.reason !== '' && (
                    <BodySmallRegular color="$red800">
                        {validationDetail.reason}
                    </BodySmallRegular>
                )}
        </YStack>
    );
};

const RuleComparisonCard = ({
    row,
    expectedRule,
    actualRule,
    validationDetail,
}: {
    row: BulkSimulationResultItem;
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
    validationDetail?: RuleValidationDetail;
}) => {
    const accountMetadata = getRuleAccountMetadata({
        expectedRule,
        actualRule,
    });
    const accountName = getRuleAccountName({
        accountMetadata,
        expectedRule,
        actualRule,
    });
    const accountNumber = getRuleAccountNumber(accountMetadata);
    const ytdTransactions = getYtdTransactionsForRule({
        row,
        expectedRule,
        actualRule,
    });

    if (expectedRule == null && actualRule == null) {
        return null;
    }

    return (
        <YStack
            padding={16}
            borderRadius={16}
            backgroundColor="$gray200"
            gap={16}
        >
            <RuleAccountHeader
                accountName={accountName}
                accountNumber={accountNumber}
            />
            <ExperimentalValidationDetail validationDetail={validationDetail} />
            <XStack gap={16} alignItems="flex-start">
                <YStack flex={1} minWidth={0}>
                    <ExpectedRulePanel rule={expectedRule} />
                </YStack>
                <YStack flex={1} minWidth={0}>
                    <ActualRulePanel
                        rule={actualRule}
                        ytdTransactions={ytdTransactions}
                        sourceAccount={row.simulationResult?.sourceAccount}
                    />
                </YStack>
            </XStack>
        </YStack>
    );
};

const getMatchingSimulationRule = (
    expectedRule: IncomeSplitRule,
    simulationRules: RulePlan[],
): RulePlan | undefined =>
    simulationRules.find((simulationRule) => {
        return [
            simulationRule.id,
            simulationRule.originalId,
            simulationRule.originalRule?.id,
            simulationRule.originalRule?.originalId,
        ].includes(expectedRule.id ?? '');
    });

const getUnmatchedSimulationRules = (
    expectedRules: IncomeSplitRule[],
    simulationRules: RulePlan[],
): RulePlan[] => {
    const expectedRuleIds = new Set(
        expectedRules.flatMap((rule) =>
            [rule.id, rule.originalId].filter(
                (id): id is string => id != null && id !== '',
            ),
        ),
    );

    return simulationRules.filter((simulationRule) => {
        return ![
            simulationRule.id,
            simulationRule.originalId,
            simulationRule.originalRule?.id,
            simulationRule.originalRule?.originalId,
        ].some((id) => id != null && expectedRuleIds.has(id));
    });
};

const getValidationDetailForRule = ({
    expectedRule,
    actualRule,
    validationDetails,
}: {
    expectedRule?: IncomeSplitRule;
    actualRule?: RulePlan;
    validationDetails: RuleValidationDetail[];
}) => {
    const ruleIds = new Set(getRuleLookupIds({ expectedRule, actualRule }));

    return validationDetails.find((detail) => ruleIds.has(detail.ruleId));
};

const RuleComparisonRows = ({
    row,
    ruleSet,
    simulationRuleSet,
    validationDetails,
}: {
    row: BulkSimulationResultItem;
    ruleSet: IncomeSplitRuleSet;
    simulationRuleSet?: RuleSetPlan;
    validationDetails: RuleValidationDetail[];
}) => {
    const expectedRules = [...ruleSet.rules].sort(
        (a, b) => a.priority - b.priority,
    );
    const simulationRules = [...(simulationRuleSet?.rules ?? [])].sort(
        (a, b) => a.priority - b.priority,
    );
    const unmatchedSimulationRules = getUnmatchedSimulationRules(
        expectedRules,
        simulationRules,
    );

    return (
        <YStack gap={12}>
            {expectedRules.map((expectedRule) => {
                const matchingSimulationRule = getMatchingSimulationRule(
                    expectedRule,
                    simulationRules,
                );

                return (
                    <RuleComparisonCard
                        key={expectedRule.id}
                        row={row}
                        expectedRule={expectedRule}
                        actualRule={matchingSimulationRule}
                        validationDetail={getValidationDetailForRule({
                            expectedRule,
                            actualRule: matchingSimulationRule,
                            validationDetails,
                        })}
                    />
                );
            })}
            {unmatchedSimulationRules.map((simulationRule) => (
                <RuleComparisonCard
                    key={simulationRule.id}
                    row={row}
                    actualRule={simulationRule}
                    validationDetail={getValidationDetailForRule({
                        actualRule: simulationRule,
                        validationDetails,
                    })}
                />
            ))}
        </YStack>
    );
};

const AlignedIncomeSplitComparison = ({
    row,
}: {
    row: BulkSimulationResultItem;
}) => {
    const incomeSplitConfig = row.incomeSplitConfig;
    const result = row.simulationResult;

    if (incomeSplitConfig == null || result == null) {
        return (
            <BodySmallRegular color="$gray1000">
                No income split comparison returned.
            </BodySmallRegular>
        );
    }

    const incomeSplitWithAccountMetadata = withCurrentRuleAccountMetadata(
        incomeSplitConfig,
        getCurrentRuleMetadataById(row),
    );
    const sortedCurrentRuleSets = getSortedCurrentRuleSets(
        incomeSplitWithAccountMetadata,
    );

    return (
        <YStack gap={16}>
            <XStack gap={16} alignItems="flex-start">
                <YStack flex={1} gap={12} minWidth={0}>
                    <BodyMediumEmphasized>Expected</BodyMediumEmphasized>
                    <CurrentIncomeSplitDetails
                        incomeSplitConfig={incomeSplitWithAccountMetadata}
                    />
                </YStack>
                <YStack flex={1} gap={12} minWidth={0}>
                    <BodyMediumEmphasized>What Happened</BodyMediumEmphasized>
                    <YStack
                        gap={8}
                        borderRadius={16}
                        borderWidth={1}
                        borderColor="$gray600"
                        backgroundColor="$gray200"
                        padding={16}
                    >
                        <BodyMediumRegular>
                            Total Income:{' '}
                            {centsToFormattedDollars(result.totalIncomeAmount)}
                        </BodyMediumRegular>
                        <BodyMediumRegular>
                            Total Allocated:{' '}
                            {centsToFormattedDollars(
                                result.totalAllocatedAmount,
                            )}
                        </BodyMediumRegular>
                        <BodyMediumRegular>
                            Remaining Funds:{' '}
                            {centsToFormattedDollars(
                                result.finalRemainingFunds,
                            )}
                        </BodyMediumRegular>
                    </YStack>
                </YStack>
            </XStack>

            {sortedCurrentRuleSets.map((ruleSet) => {
                const matchingSimulationRuleSet = getMatchingSimulationRuleSet(
                    ruleSet,
                    result.ruleSetPlans,
                );

                return (
                    <YStack
                        key={ruleSet.id}
                        gap={12}
                        borderRadius={20}
                        borderWidth={1}
                        borderColor="$gray500"
                        backgroundColor="$white"
                        padding={16}
                    >
                        <View
                            paddingVertical={8}
                            paddingHorizontal={24}
                            borderRadius={16}
                            backgroundColor="$gray200"
                            alignItems="center"
                            justifyContent="center"
                            height={48}
                        >
                            <BodySmallRegular fontStyle="italic">
                                {ruleSet.name}
                            </BodySmallRegular>
                        </View>
                        <RuleComparisonRows
                            row={row}
                            ruleSet={ruleSet}
                            simulationRuleSet={matchingSimulationRuleSet}
                            validationDetails={result.ruleValidationDetails}
                        />
                    </YStack>
                );
            })}

            <XStack gap={16} alignItems="flex-start">
                <YStack flex={1} minWidth={0}>
                    <IncomeSplitEndOfRules />
                </YStack>
                <YStack flex={1} minWidth={0} />
            </XStack>
        </YStack>
    );
};

const ResultDetailsBody = (row: BulkSimulationResultItem) => {
    const result = row.simulationResult;
    const simulationExecutionId = result?.incomeSplitExecutionId;

    return (
        <YStack gap={16} padding={16} backgroundColor="$gray100">
            <LineageAuditPanel finding={row.lineageAudit} />
            <XStack gap={24} flexWrap="wrap">
                <YStack gap={4}>
                    <LabelRegular color="$gray1000">
                        Simulation execution
                    </LabelRegular>
                    {simulationExecutionId != null ? (
                        <>
                            <MonoLabelRegular>
                                {simulationExecutionId}
                            </MonoLabelRegular>
                            <a
                                href={getDatadogIncomeSplitExecutionUrl(
                                    simulationExecutionId,
                                )}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    color: '#00875a',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                }}
                            >
                                View in Datadog
                            </a>
                        </>
                    ) : (
                        <MonoLabelRegular>N/A</MonoLabelRegular>
                    )}
                </YStack>
            </XStack>

            {result != null && <AlignedIncomeSplitComparison row={row} />}
        </YStack>
    );
};

const getMembershipLabel = (row: BulkSimulationResultItem) => {
    const firstUser = row.membership?.users[0];
    const name = [firstUser?.firstName, firstUser?.lastName]
        .filter((part) => part != null && part !== '')
        .join(' ');

    return name !== '' ? name : row.membershipId;
};

const ResultListItem = ({
    row,
    isSelected,
    onPress,
}: {
    row: BulkSimulationResultItem;
    isSelected: boolean;
    onPress: () => void;
}) => (
    <View
        onPress={onPress}
        cursor="pointer"
        borderRadius={16}
        borderWidth={1}
        borderColor={isSelected ? '$green900' : '$gray500'}
        backgroundColor={isSelected ? '$green300' : '$white'}
        padding={12}
        gap={12}
        hoverStyle={{ borderColor: '$green900' }}
    >
        <XStack justifyContent="space-between" alignItems="center" gap={12}>
            <YStack gap={4} flex={1} minWidth={0}>
                <BodyMediumEmphasized>
                    {getMembershipLabel(row)}
                </BodyMediumEmphasized>
                <MonoLabelRegular color="$gray1000">
                    {row.membershipId}
                </MonoLabelRegular>
            </YStack>
            <StatusPill status={row.status} />
        </XStack>
        <SourceBody {...row} />
        <SimulationSummaryBody {...row} />
        <LineageAuditBadge finding={row.lineageAudit} />
        {(row.error != null && row.error !== '') ||
        row.validationErrors.length > 0 ? (
            <ErrorBody {...row} />
        ) : null}
    </View>
);

const getSweepMemberFinding = (
    member: LineageAuditSweepMember,
): SimulatorLineageFinding | undefined => {
    if (member.finding != null) {
        return member.finding;
    }
    if (member.lineageAudit != null) {
        return member.lineageAudit;
    }
    // Finding fields flattened onto the member itself.
    if (typeof member.hasLostLineage === 'boolean') {
        return {
            hasLostLineage: member.hasLostLineage,
            hasRealizedOverContribution:
                member.hasRealizedOverContribution ?? false,
            affectedRuleIds: member.affectedRuleIds ?? [],
            totalOrphanedYearlyCents: member.totalOrphanedYearlyCents ?? 0,
            totalRealizedOverContributionCents:
                member.totalRealizedOverContributionCents ?? 0,
            overContributionMonths: member.overContributionMonths ?? [],
        };
    }
    return undefined;
};

const SweepMemberCard = ({ member }: { member: LineageAuditSweepMember }) => (
    <YStack
        gap={8}
        padding={12}
        borderRadius={16}
        borderWidth={1}
        borderColor="$gray500"
        backgroundColor="$white"
    >
        <YStack gap={2}>
            <BodyMediumEmphasized>
                {member.membershipId ?? 'Unknown membership'}
            </BodyMediumEmphasized>
            {member.userId != null && (
                <MonoLabelRegular color="$gray1000">
                    {member.userId}
                </MonoLabelRegular>
            )}
        </YStack>
        <LineageAuditPanel finding={getSweepMemberFinding(member)} />
    </YStack>
);

const SweepRawResponse = ({ sweep }: { sweep: LineageAuditSweepResponse }) => {
    const [showRaw, setShowRaw] = useState(false);

    return (
        <YStack gap={6}>
            <XStack
                onPress={() => {
                    setShowRaw((current) => !current);
                }}
                cursor="pointer"
                alignItems="center"
                gap={8}
            >
                <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                    Raw response
                </BodySmallRegular>
                <Icon
                    icon={showRaw ? ChevronDownIcon : ChevronRightIcon}
                    size={16}
                    color="$gray1000"
                />
            </XStack>
            {showRaw && (
                <View padding={12} borderRadius={8} backgroundColor="$gray100">
                    <pre
                        style={{
                            margin: 0,
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {JSON.stringify(sweep, null, 2)}
                    </pre>
                </View>
            )}
        </YStack>
    );
};

const SweepResults = ({ sweep }: { sweep: LineageAuditSweepResponse }) => {
    const members: LineageAuditSweepMember[] = sweep.affected ?? [];
    const { summary } = sweep;

    return (
        <YStack gap={16}>
            <XStack gap={12} flexWrap="wrap">
                <SummaryTile
                    label="Scanned configs"
                    value={summary.scannedConfigs}
                />
                <SummaryTile
                    label="Affected memberships"
                    value={summary.affectedMemberships}
                    tone={
                        summary.affectedMemberships > 0 ? 'danger' : 'positive'
                    }
                />
                <SummaryTile
                    label="Latent (lineage lost)"
                    value={summary.latentMemberships}
                    tone={summary.latentMemberships > 0 ? 'danger' : 'neutral'}
                />
                <SummaryTile
                    label="Realized over-contribution"
                    value={summary.realizedMemberships}
                    tone={
                        summary.realizedMemberships > 0 ? 'danger' : 'neutral'
                    }
                />
            </XStack>

            {members.length > 0 ? (
                <YStack gap={12}>
                    {members.map((member, index) => (
                        <SweepMemberCard
                            key={member.membershipId ?? index}
                            member={member}
                        />
                    ))}
                </YStack>
            ) : (
                <BodyMediumRegular color="$gray1000">
                    No affected members found.
                </BodyMediumRegular>
            )}

            <SweepRawResponse sweep={sweep} />
        </YStack>
    );
};

const LineageAuditSweepSection = () => {
    const [membershipIdsInput, setMembershipIdsInput] = useState('');
    const [limitInput, setLimitInput] = useState('');
    const [monthInput, setMonthInput] = useState('');

    const membershipIds = useMemo(
        () => parseMembershipIds(membershipIdsInput),
        [membershipIdsInput],
    );
    const limit = useMemo(() => parseLimit(limitInput), [limitInput]);
    const isLimitInvalid = limitInput.trim() !== '' && limit == null;
    const affectedInMonth =
        monthInput.trim() !== '' ? monthInput.trim() : undefined;

    const {
        data: sweep,
        isFetching,
        isError,
        error,
        refetch,
    } = useIncomeSplitLineageAuditSweep(
        {
            ...(membershipIds != null ? { membershipIds } : {}),
            ...(limit != null ? { limit } : {}),
            ...(affectedInMonth != null ? { affectedInMonth } : {}),
        },
        {
            enabled: false,
        },
    );

    return (
        <YStack
            gap={16}
            borderTopWidth={1}
            borderColor="$gray500"
            paddingTop={32}
        >
            <YStack gap={8}>
                <H5Emphasized>Lineage Audit Sweep</H5Emphasized>
                <BodyMediumRegular color="$gray1000">
                    Scans members for income-split rules that lost their
                    original lineage (contribution caps undercounting).
                    Read-only admin diagnostic — does not move money or change
                    any plan. All filters are optional.
                </BodyMediumRegular>
            </YStack>

            <XStack gap={16} alignItems="flex-end" flexWrap="wrap">
                <YStack gap={8} flex={1} minWidth={360}>
                    <LabelRegular>Membership IDs</LabelRegular>
                    <Input
                        value={membershipIdsInput}
                        onChangeText={setMembershipIdsInput}
                        placeholder="membershipId1, membershipId2 (optional)"
                    />
                </YStack>
                <YStack gap={8} width={160}>
                    <LabelRegular>Affected in month</LabelRegular>
                    <Input
                        value={monthInput}
                        onChangeText={setMonthInput}
                        placeholder="2026-10"
                    />
                </YStack>
                <YStack gap={8} width={120}>
                    <LabelRegular>Limit</LabelRegular>
                    <Input
                        value={limitInput}
                        onChangeText={setLimitInput}
                        placeholder="500"
                        invalid={isLimitInvalid}
                    />
                </YStack>
                <Button
                    size="small"
                    disabled={isFetching || isLimitInvalid}
                    onPress={() => {
                        void refetch();
                    }}
                >
                    {isFetching
                        ? 'Running sweep...'
                        : 'Run lineage audit sweep'}
                </Button>
            </XStack>

            {isLimitInvalid && (
                <BodySmallRegular color="$red1000">
                    Limit must be a positive whole number.
                </BodySmallRegular>
            )}

            {isError && (
                <BodySmallRegular color="$red1000">
                    {error instanceof Error
                        ? error.message
                        : 'Lineage audit sweep failed.'}
                </BodySmallRegular>
            )}

            {sweep != null && <SweepResults sweep={sweep} />}
        </YStack>
    );
};

export const BulkIncomeSplitSimulationPage = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const canUseBulkSimulation =
        adminUser.authorization.betaTransactionFeatures.canUse;
    const [membershipIdsInput, setMembershipIdsInput] = useState('');
    const [limitInput, setLimitInput] = useState('');
    const [result, setResult] = useState<BulkSimulationResponse | null>(null);
    const [selectedResultId, setSelectedResultId] = useState<string | null>(
        null,
    );
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const toast = useToastController();
    const { mutateAsync: simulateBulkIncomeSplit, isPending } =
        useSimulateBulkIncomeSplit({
            onError: (error) => {
                toast.show(
                    'Bulk simulation failed',
                    MutationErrorToastOptions(
                        error instanceof Error ? error.message : String(error),
                    ),
                );
            },
        });

    const membershipIds = useMemo(
        () => parseMembershipIds(membershipIdsInput),
        [membershipIdsInput],
    );
    const limit = useMemo(() => parseLimit(limitInput), [limitInput]);
    const isLimitInvalid = limitInput.trim() !== '' && limit == null;
    const filteredResults = useMemo(() => {
        if (result == null) {
            return [];
        }

        return result.results.filter(STATUS_FILTER_PREDICATES[statusFilter]);
    }, [result, statusFilter]);

    const totalLineageIssues = useMemo(
        () =>
            result?.results.filter(
                (row) => lineageVerdict(row.lineageAudit) !== 'pass',
            ).length ?? 0,
        [result],
    );

    const selectedResult = useMemo(() => {
        if (result == null) {
            return null;
        }

        return (
            result.results.find(
                (row) => row.originalExecutionId === selectedResultId,
            ) ??
            filteredResults[0] ??
            null
        );
    }, [result, selectedResultId, filteredResults]);

    if (!canUseBulkSimulation) {
        return (
            <FullPageContainer>
                <BodyMediumEmphasized color="$red1100">
                    No access to bulk income split simulation.
                </BodyMediumEmphasized>
            </FullPageContainer>
        );
    }

    const runSimulation = async () => {
        if (isLimitInvalid) {
            return;
        }

        try {
            const response = await simulateBulkIncomeSplit({
                body: {
                    ...(limit != null ? { limit } : {}),
                    ...(membershipIds != null ? { membershipIds } : {}),
                },
            });
            setSelectedResultId(
                response.results[0]?.originalExecutionId ?? null,
            );
            setResult(response);
        } catch {
            // Error toast is handled by the mutation's onError callback.
        }
    };

    return (
        <FullPageContainer alignItems="stretch" gap={32}>
            <YStack gap={8}>
                <H5Emphasized>Bulk Income Split Simulation</H5Emphasized>
                <BodyMediumRegular color="$gray1000">
                    Simulate a bulk income split drop. Membership IDs are
                    optional; leave blank to let the backend select up to the
                    limit.
                </BodyMediumRegular>
            </YStack>

            <XStack gap={16} alignItems="flex-end" flexWrap="wrap">
                <YStack gap={8} flex={1} minWidth={360}>
                    <LabelRegular>Membership IDs</LabelRegular>
                    <Input
                        value={membershipIdsInput}
                        onChangeText={setMembershipIdsInput}
                        placeholder="membershipId1, membershipId2, membershipId3"
                    />
                </YStack>
                <YStack gap={8} width={160}>
                    <LabelRegular>Limit</LabelRegular>
                    <Input
                        value={limitInput}
                        onChangeText={setLimitInput}
                        placeholder="100"
                        invalid={isLimitInvalid}
                    />
                </YStack>
                <Button
                    size="small"
                    disabled={isPending || isLimitInvalid}
                    onPress={() => {
                        void runSimulation();
                    }}
                >
                    {isPending ? 'Simulating...' : 'Simulate'}
                </Button>
            </XStack>

            {isLimitInvalid && (
                <BodySmallRegular color="$red1000">
                    Limit must be a positive whole number.
                </BodySmallRegular>
            )}

            {result != null && (
                <YStack gap={24}>
                    <XStack gap={12} flexWrap="wrap">
                        <SummaryTile
                            label="Requested"
                            value={result.totalRequested}
                            isActive={statusFilter === 'all'}
                            onPress={() => {
                                setStatusFilter('all');
                            }}
                        />
                        <SummaryTile
                            label="Simulated"
                            value={result.totalSimulated}
                            isActive={statusFilter === 'simulated'}
                            onPress={() => {
                                setStatusFilter('simulated');
                            }}
                        />
                        <SummaryTile
                            label="Succeeded"
                            value={result.totalSucceeded}
                            tone="positive"
                            isActive={statusFilter === 'succeeded'}
                            onPress={() => {
                                setStatusFilter('succeeded');
                            }}
                        />
                        <SummaryTile
                            label="Failed"
                            value={result.totalFailed}
                            tone="danger"
                            isActive={statusFilter === 'failed'}
                            onPress={() => {
                                setStatusFilter('failed');
                            }}
                        />
                        <SummaryTile
                            label="Validation errors"
                            value={result.totalWithValidationErrors}
                            tone={
                                result.totalWithValidationErrors > 0
                                    ? 'danger'
                                    : 'neutral'
                            }
                            isActive={statusFilter === 'validation_errors'}
                            onPress={() => {
                                setStatusFilter('validation_errors');
                            }}
                        />
                        <SummaryTile
                            label="Lineage issues"
                            value={totalLineageIssues}
                            tone={totalLineageIssues > 0 ? 'danger' : 'neutral'}
                            isActive={statusFilter === 'lineage_issues'}
                            onPress={() => {
                                setStatusFilter('lineage_issues');
                            }}
                        />
                    </XStack>

                    <XStack gap={16} alignItems="flex-start">
                        <YStack gap={12} width={360} flexShrink={0}>
                            <BodyMediumEmphasized>
                                Simulation Results ({filteredResults.length})
                            </BodyMediumEmphasized>
                            <ScrollView maxHeight="70vh">
                                <YStack gap={8} paddingRight={8}>
                                    {filteredResults.map((row) => (
                                        <ResultListItem
                                            key={row.originalExecutionId}
                                            row={row}
                                            isSelected={
                                                selectedResult?.originalExecutionId ===
                                                row.originalExecutionId
                                            }
                                            onPress={() => {
                                                setSelectedResultId(
                                                    row.originalExecutionId,
                                                );
                                            }}
                                        />
                                    ))}
                                </YStack>
                            </ScrollView>
                        </YStack>
                        <YStack flex={1} minWidth={0}>
                            {selectedResult != null ? (
                                ResultDetailsBody(selectedResult)
                            ) : (
                                <YStack
                                    padding={16}
                                    borderRadius={16}
                                    backgroundColor="$gray100"
                                >
                                    <BodyMediumRegular color="$gray1000">
                                        Select a simulation result to inspect.
                                    </BodyMediumRegular>
                                </YStack>
                            )}
                        </YStack>
                    </XStack>
                </YStack>
            )}

            <LineageAuditSweepSection />
        </FullPageContainer>
    );
};
